/** external */
import _ from "lodash";
import React, { useEffect, memo, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import classNames from "classnames";
import { graphql, compose } from "lib/apolloClient";

/** internal */
import { WORKSHEET_QUESTION_CREATION } from "Constants/stringConstants";
import { useI18n } from "Hooks";
import AddQuestionsDropdownV2 from "Worksheet/components/AddQuestionsDropdownV2";
import { getSelectedAcademicYearId } from "modules/CommonSelectors";
import { getAIConversationsQuery } from "Worksheet/modules/WorksheetQueries";
import { getAIConversationsFromCache } from "Worksheet/modules/WorksheetGraphqlHelpers";
import { getAssessmentQuery } from "Assessments/modules/AssessmentQuery";

/** relative */
import {
  createSection,
  updateLoadingState,
  createParentResource,
  importQuestionFromLibrary,
  updateActiveQuestion,
  createQuestion,
  updateIsAIQuestionGenerationEnabled,
  updateExtractFromDoc,
  updateWorksheetSidebar,
  createQuestionV2,
  importQuestionFromLibraryV2,
  updateWorksheetAIPanel,
} from "../../modules/WorksheetModules";
import {
  ORG_RESOURCE_TYPE_WORKSHEET,
  RESOURCE_TYPE_FOLDER,
  WORKSHEET_MODES,
  RESOURCE_TYPE_QUESTION,
  MODE_TEACHER_CREATE,
  GENERATE_USING_AI,
  EXTRACT_FROM_DOC,
  WORKSHEET_ELEMENT_IDS,
  SECTION_AND_NODE_KEY,
  ADD_QUESTION_FROM_LIBRARY,
} from "../../modules/WorksheetConstants";
import {
  convertNumberToAlphabet,
  getAssessmentDetailsData,
  getDetailsFromSections,
  getSideBarOptions,
  getOrganizationResources,
  getLearningGoalsMappingFromSections,
  createTypeSessionStorage,
  getSectionTitle,
  getAiConversationId,
  getSideBarOptionsV2,
  isWorksheetRevampV3,
  getAIPanelContextOptions,
} from "../../modules/WorksheetUtils";
import { isWorksheetMutationsV2Enabled } from "../../modules/WorksheetFeatureFlags";
import classes from "./CreateWorksheetV2.scss";
import SideBar from "../SideBar";
import AiQuestionGenerator from "../AiQuestionGenerator";
import ExtractFromDoc from "../ExtractFromDoc";
import {
  SinglePageQuestionEditor,
  SinglePageQuestionEditorV2,
} from "./SinglePageQuestionEditor";
import {
  getLearningGoalsMappingFromSectionsV2,
  getDetailsFromSectionsV2,
} from "./CreateWorksheetV2Utils";
import CreateWorksheetSidebarWrapper from "./CreateWorksheetSidebarWrapper";
import WorksheetAIPanel from "./WorksheetAIPanel";

/**
 * ExtractRunningProgressUpdater component updates the progress bar for document extraction.
 *
 * The useEffect hook creates an interval that incrementally updates the progress bar
 * during document extraction. It increases the running progress by a random value
 * between 0-3 every 1.5 seconds, until certain thresholds are reached:
 *
 * - When progress reaches 100% (extraction complete)
 * - When running progress reaches 99% (about to complete)
 * - When running progress exceeds progress+25% (throttling to stay within bounds)
 *
 * This provides visual feedback to users during potentially long-running extraction processes.
 *
 * @component
 */
const ExtractRunningProgressUpdater = React.memo(
  ({ parentId, progress, runningProgress, updateExtractFromDoc }) => {
    useEffect(() => {
      const interval = setInterval(() => {
        const isProgressCompleted = progress === 100;
        const isRunningProgressAboutToComplete = runningProgress === 99;
        const isAtRunningProgressThreshold = runningProgress >= progress + 25;
        if (
          isProgressCompleted ||
          isAtRunningProgressThreshold ||
          isRunningProgressAboutToComplete
        ) {
          clearInterval(interval);
          return;
        }

        updateExtractFromDoc({
          assessmentId: parentId,
          data: {
            runningProgress: Math.min(
              runningProgress + _.random(0, 3, false),
              progress + 25
            ),
            isExtracting: true,
          },
        });
      }, 1500);

      return () => clearInterval(interval);
    }, [progress, runningProgress]);

    return null;
  }
);
ExtractRunningProgressUpdater.displayName = "ExtractRunningProgressUpdater";

const CreateWorksheetV2 = memo(props => {
  const {
    data: sections,
    mode,
    parentId,
    parentType,
    createParentResource,
    createSection,
    importQuestionFromLibrary,
    setQuestionRef,
    setWorksheetContainerRef,
    parentResourceId,
    defaultQuestionIds,
    showWorksheetAssistant,
    activeQuestionId,
    updateActiveQuestion,
    createQuestion,
    assessmentDetailsComponent,
    worksheetDetailsRef,
    sourceFrom,
    classSubjects,
    creationMode,
    unitPlanTemplateId,
    isGenWithAIEnabled,
    updateIsAIQuestionGenerationEnabled,
    updateExtractFromDoc,
    updateWorksheetSidebar,
    updateLoadingState,
    isGeneratingAIQuestions,
    courseId,
    selectedCourse,
    unitPlanId,
    isFromUnitPlan,
    isDraft,
    isTeacherPreviewModeEnabled,
    isToddleLibraryPortal,
    isSidebarOpen,
    isExtracting,
    progress,
    runningProgress,
    conversationMetadataExtract,
    conversationAttachmentsExtract,
    portalType,
    createQuestionV2,
    parentItemId,
    aiConversationId,
    extractionConversationId,
    assessmentDetails,
    assessmentToolConversationId,
    assessmentTools,
    getExtractionAIConversationsRefetch,
    getAssessmentRefetch,
    isWorksheetExtractionInProgress,
    importQuestionFromLibraryV2,
    updateWorksheetAIPanel,
    isQueriesV2Enabled,
    aiPromptFromMetaData,
  } = props;

  const isWorksheetAIRevamp = isWorksheetRevampV3();
  const isMutationsV2Enabled = isWorksheetMutationsV2Enabled();

  if (isGeneratingAIQuestions && isGenWithAIEnabled) {
    updateIsAIQuestionGenerationEnabled(false);
  }

  const updateActiveQuestionId = useCallback(
    id => {
      updateActiveQuestion(id);
    },
    [updateActiveQuestion]
  );

  const handleAiQuestionGenerator = useCallback(
    flag => {
      updateIsAIQuestionGenerationEnabled(flag);
    },
    [updateIsAIQuestionGenerationEnabled]
  );

  const handleExtractFromDoc = useCallback(() => {
    updateActiveQuestion(EXTRACT_FROM_DOC);
  }, [updateActiveQuestion]);

  const { t } = useI18n(["common"]);

  useEffect(() => {
    const defaultTypeInSession = createTypeSessionStorage("GET");

    if (isWorksheetAIRevamp) {
      // * For < 1400px width, close the sidebar in new worksheet revamp v2
      // * Always open create screen, since everything is in single page now
      updateWorksheetSidebar({ isOpen: window.innerWidth >= 1400 });
      if (defaultTypeInSession === ADD_QUESTION_FROM_LIBRARY) {
        updateActiveQuestion(ADD_QUESTION_FROM_LIBRARY);
      } else {
        updateActiveQuestion(WORKSHEET_QUESTION_CREATION);
      }
    } else if (defaultTypeInSession) {
      // * Using defaultTypeInSession to determine the default active question
      // * If ai or extraction, close sidebar and open the respective panel
      const defaultActive = defaultTypeInSession;
      updateActiveQuestion(defaultActive);
      const isGenUsingAI = defaultActive === GENERATE_USING_AI;
      const isExtracting = defaultActive === EXTRACT_FROM_DOC;
      const isExternal = isGenUsingAI || isExtracting;
      updateWorksheetSidebar({ isOpen: !isExternal });
    } else {
      // * If no defaultTypeInSession, open the sidebar and set the default create
      updateActiveQuestion(WORKSHEET_QUESTION_CREATION);
    }

    // * Resetting the default active question in the sidebar
    updateWorksheetSidebar({ defaultActive: null });

    /** In toddle library portal, while creating a new Worksheet to import a question,
       a section is created, and the question is imported immediately. */
    if (!_.isEmpty(defaultQuestionIds) && _.isEmpty(sections)) {
      addSection();
    }
  }, []);

  useEffect(() => {
    if (aiConversationId && isWorksheetAIRevamp) {
      updateWorksheetAIPanel({ conversationId: aiConversationId });
    }
  }, [aiConversationId, isWorksheetAIRevamp]);

  useEffect(() => {
    if (!_.isEmpty(aiPromptFromMetaData)) {
      // * Delays the execution of updateActiveQuestion until after the current call stack clears
      setTimeout(() => {
        updateActiveQuestion(GENERATE_USING_AI);
      }, 0);
    }
  }, [aiPromptFromMetaData]);

  const importDefaultQuestions = useCallback(
    async ({ sectionId }) => {
      if (!_.isEmpty(defaultQuestionIds) && !_.isEmpty(sectionId)) {
        updateLoadingState(true);

        try {
          if (isMutationsV2Enabled) {
            await importQuestionFromLibraryV2({
              questionIds: defaultQuestionIds,
              parentItemId: sectionId,
            });
          } else {
            await importQuestionFromLibrary({
              questionIds: defaultQuestionIds,
              parentResourceId: sectionId,
            });
          }
        } finally {
          updateLoadingState(false);
        }
      }
    },
    [defaultQuestionIds, importQuestionFromLibrary, updateLoadingState]
  );

  const addSection = useCallback(async () => {
    const sectionKey = convertNumberToAlphabet(_.size(sections));
    const isEmptySections = _.isEmpty(sections);
    let sectionId = null;

    if (isMutationsV2Enabled) {
      let _parentItemId = parentItemId;

      // Create parent resource if needed (first section)
      if (isEmptySections && _.isNil(_parentItemId)) {
        _parentItemId = await createQuestionV2({
          parentId,
          parentType,
          questionTypeId: SECTION_AND_NODE_KEY,
          isQueriesV2Enabled,
          setQuestionAsActive: false, // Do not set parent resource as active
        });
      }

      // Create the section if parent exists
      if (!_.isEmpty(_parentItemId)) {
        sectionId = await createQuestionV2({
          parentId: _parentItemId,
          parentType: "QUESTION",
          questionTypeId: SECTION_AND_NODE_KEY,
          putAfterQuestionId: _.get(_.last(sections), "item.id", null),
          fields: getSectionTitle(sectionKey),
          isQueriesV2Enabled,
          setQuestionAsActive: false, // Do not set section as active
        });
      }
    } else {
      let _parentResourceId = parentResourceId;

      // Create parent resource if needed (first section)
      if (isEmptySections && _.isNil(_parentResourceId)) {
        _parentResourceId = await createParentResource({
          parentId,
          parentType,
          organizationResources: [
            {
              type: ORG_RESOURCE_TYPE_WORKSHEET,
              resourceType: RESOURCE_TYPE_FOLDER,
            },
          ],
        });
      }

      // Create the section if parent exists
      if (!_.isEmpty(_parentResourceId)) {
        sectionId = await createSection({
          parentId,
          parentType,
          parentResourceId: _parentResourceId,
          organizationResources: getOrganizationResources(sectionKey),
          putAfterItemId: _.get(_.last(sections), "id", null),
        });
      }
    }

    // Import default questions for the initial section creation
    if (isEmptySections) {
      importDefaultQuestions({ sectionId });
    }

    return { sectionId };
  }, [
    sections,
    createSection,
    parentId,
    parentType,
    parentResourceId,
    importDefaultQuestions,
    createParentResource,
    isMutationsV2Enabled,
    parentItemId,
    createQuestionV2,
  ]);

  const addQuestion = useCallback(
    async ({ key, putAfterItemId, sectionId }) => {
      // If no section ID is provided, create a new section first
      let _sectionId = sectionId;
      if (_.isEmpty(_sectionId)) {
        const { sectionId } = await addSection();
        _sectionId = sectionId;
      }

      const organizationResources = [
        {
          type: ORG_RESOURCE_TYPE_WORKSHEET,
          resourceType: RESOURCE_TYPE_QUESTION,
        },
      ];

      // Use V2 mutation format if enabled, otherwise use V1
      if (isMutationsV2Enabled) {
        await createQuestionV2({
          parentId: _sectionId,
          parentType: "QUESTION",
          questionTypeId: key,
          putAfterQuestionId: putAfterItemId,
          isQueriesV2Enabled,
        });
      } else {
        await createQuestion({
          parentResourceId: _sectionId,
          questionTypeId: key,
          organizationResources,
          putAfterItemId,
        });
      }
    },
    [addSection, createQuestion, createQuestionV2, isMutationsV2Enabled]
  );

  const toggleAiQuestionGenerator = useCallback(
    (id, isLastQuestion) => {
      updateActiveQuestion(null);
      if (isLastQuestion) {
        updateIsAIQuestionGenerationEnabled(false);
      }
    },
    [updateActiveQuestion, updateIsAIQuestionGenerationEnabled]
  );

  const { options, questionCount, totalMarks } = useMemo(() => {
    //WORKSHEET_RESTRUCTURING: Use getErrorCountV2 when queriesV2 is enabled
    const _getSideBarOptions = isQueriesV2Enabled
      ? getSideBarOptionsV2
      : getSideBarOptions;

    const { questionCount, totalMarks, options } = _getSideBarOptions({
      data: sections,
      mode,
      t,
    });

    return { options, questionCount, totalMarks };
  }, [mode, sections, t, activeQuestionId, isQueriesV2Enabled]);

  const showGenerateWithAI = _.isEqual(activeQuestionId, GENERATE_USING_AI);
  const showExtractFromDoc = _.isEqual(activeQuestionId, EXTRACT_FROM_DOC);

  const aiModalContainerStyles = classNames({
    [classes.showAIQuestionModal]: showGenerateWithAI,
    [classes.hideAIQuestionModal]: !showGenerateWithAI,
  });

  const contentClasses = classNames(classes.worksheetMiddlePane, {
    [classes.worksheetContentSidebarOpen]: isSidebarOpen,
  });

  // function for getting learningGoalsMapping (learning goals mapping has learning goals, mapped to questions)
  //WORKSHEET_RESTRUCTURING: Use getLearningGoalsMappingFromSectionsV2 when queriesV2 is enabled
  const _getLearningGoalsMappingFromSections = isQueriesV2Enabled
    ? getLearningGoalsMappingFromSectionsV2
    : getLearningGoalsMappingFromSections;

  const getLearningGoalsMappingWrapper = useCallback(
    learningGoalsFields => {
      return _getLearningGoalsMappingFromSections({
        sections,
        learningGoalsFields,
        t,
      });
    },
    [sections, t, _getLearningGoalsMappingFromSections]
  );

  const getAddButton = useCallback(
    ({
      forSidebar = false,
      isRevampedSinglePageUI = false,
      btnComponent = null,
      isAddDropdownButtonRevamp = false,
      showAddSection = true,
      showAddSeparator = false,
      showAddInlineText = false,
      setAddButtonDropdownActiveId = _.noop,
      isSubQuestion = false,
      showAddInstructions = false,
    }) => {
      let lastSectionId,
        lastQuestionResourceId,
        lastQuestionId,
        lastSectionItemId;
      const lastSection = _.last(options);

      if (!_.isEmpty(lastSection)) {
        const lastQuestion = _.last(_.get(lastSection, "childOptions"));
        lastSectionId = _.get(lastSection, "id");
        lastQuestionResourceId = _.get(lastQuestion, "resourceId");
        lastQuestionId = _.get(lastQuestion, "id");
        lastSectionItemId = _.get(lastSection, "parentItemId");
      }

      const addButtonClasses = classNames({
        [classes.sidebarAddButtonWrapper]: forSidebar,
        [classes.wsDetailsAddButtonWrapper]: !forSidebar,
        [classes.wsDetailsAddButtonWrapperRevamped]:
          !forSidebar && isRevampedSinglePageUI,
        [classes.sidebarAddButtonWrapperRevamped]:
          isRevampedSinglePageUI && btnComponent && forSidebar,
        [classes.wsDetailsAddButtonWrapperV2]: !forSidebar,
      });

      return (
        <div className={addButtonClasses}>
          <AddQuestionsDropdownV2
            parentResourceId={lastSectionId}
            putAfterItemId={
              isMutationsV2Enabled ? lastQuestionId : lastQuestionResourceId
            }
            onOptionClick={addQuestion}
            updateActiveQuestionId={updateActiveQuestionId}
            handleAiQuestionGenerator={handleAiQuestionGenerator}
            handleExtractFromDoc={handleExtractFromDoc}
            addSection={addSection}
            worksheetDetailsRef={worksheetDetailsRef}
            isFullWidthBtn={forSidebar}
            isLargeButton={!forSidebar}
            isToddleLibraryPortal={isToddleLibraryPortal}
            isRevampedSinglePageUI={isRevampedSinglePageUI}
            updateWorksheetSidebar={updateWorksheetSidebar}
            activeQuestionId={activeQuestionId}
            parentItemId={lastSectionItemId}
            btnComponent={btnComponent}
            showAddSection={showAddSection}
            isAddDropdownButtonRevamp={isAddDropdownButtonRevamp}
            showAddSeparator={showAddSeparator}
            showAddInlineText={showAddInlineText}
            setAddButtonDropdownActiveId={setAddButtonDropdownActiveId}
            isSubQuestion={isSubQuestion}
            showAddInstructions={showAddInstructions}
            isQueriesV2Enabled={isQueriesV2Enabled}
          />
        </div>
      );
    },
    [
      addQuestion,
      addSection,
      handleAiQuestionGenerator,
      handleExtractFromDoc,
      isToddleLibraryPortal,
      options,
      updateActiveQuestionId,
      worksheetDetailsRef,
      activeQuestionId,
      updateWorksheetSidebar,
      isMutationsV2Enabled,
    ]
  );

  const handleSidebarCollapse = () => {
    updateWorksheetSidebar({ isOpen: !isSidebarOpen });
  };

  //WORKSHEET_RESTRUCTURING: Use getDetailsFromSectionsV2 when queriesV2 is enabled
  const SinglePageQuestionEditorComp = isQueriesV2Enabled
    ? SinglePageQuestionEditorV2
    : SinglePageQuestionEditor;

  if (isWorksheetAIRevamp) {
    /**
     *
     * @description
     * Renders the CREATE WORKSHEET MODE layout for Worksheet AI Revamp
     *
     * Layout consists of three main components:
     * 1. SIDEBAR - Contains outline, question list and add button
     *    - Collapsible and floating when viewport width < 1400px
     *    - Provides worksheet structure navigation
     *
     * 2. MIDDLE PANE - Main content area displaying:
     *    - Assessment details configuration
     *    - Assessment tools settings
     *    - Sections and questions editor
     *    - Question loader with AI assistance
     *
     * 3. AI PANEL - Unified interface for all Worksheet AI operations
     *    - Provides AI-assisted content generation
     *    - Handles question creation and modification
     *    - Offers assessment tools recommendations
     *
     */
    const worksheetMiddlePaneClasses = classNames(classes.worksheetMiddlePane, {
      [classes.worksheetContentSidebarOpen]: isSidebarOpen,
    });

    const contextOptions = getAIPanelContextOptions({
      sections,
      aiConversationId,
    });

    return (
      <div
        ref={setWorksheetContainerRef}
        className={classes.createWorksheetContainer}
      >
        <div className={classes.worksheetSidebarContainer}>
          <CreateWorksheetSidebarWrapper
            showSidebar={isSidebarOpen}
            toggleSidebar={handleSidebarCollapse}
            addQuestionButton={getAddButton({
              forSidebar: true,
              showAddInlineText: true,
            })}
          >
            <SideBar
              mode={mode}
              options={options}
              hideHeader={true}
              totalMarks={totalMarks}
              parentItemId={parentItemId}
              onAddOptionClick={addSection}
              questionCount={questionCount}
              parentResourceId={parentResourceId}
            />
          </CreateWorksheetSidebarWrapper>
        </div>
        <div className={worksheetMiddlePaneClasses}>
          <SinglePageQuestionEditorComp
            t={t}
            mode={mode}
            setQuestionRef={setQuestionRef}
            showWorksheetAssistant={showWorksheetAssistant}
            classSubjects={classSubjects}
            parentId={parentId}
            portalType={portalType}
            assessmentId={parentId}
            creationMode={creationMode}
            unitPlanTemplateId={unitPlanTemplateId}
            getLearningGoalsMappingWrapper={getLearningGoalsMappingWrapper}
            worksheetDetailsRef={worksheetDetailsRef}
            courseId={courseId}
            selectedCourse={selectedCourse}
            unitPlanId={unitPlanId}
            isFromUnitPlan={isFromUnitPlan}
            isDraft={isDraft}
            sourceFrom={sourceFrom}
            sections={sections}
            getAddButton={getAddButton}
            assessmentDetailsComponent={assessmentDetailsComponent}
            assessmentToolConversationId={assessmentToolConversationId}
            assessmentTools={assessmentTools}
            assessmentDetails={assessmentDetails}
            getAssessmentRefetch={getAssessmentRefetch}
            isWorksheetAIRevamp={isWorksheetAIRevamp}
          />
        </div>
        {!isToddleLibraryPortal && isWorksheetAIRevamp && (
          <WorksheetAIPanel
            sourceFrom={sourceFrom}
            assessmentId={parentId}
            classSubjects={classSubjects}
            contextOptions={contextOptions}
            worksheetAiConversationId={aiConversationId}
          />
        )}
      </div>
    );
  }

  return (
    <div
      className={classes.createWorksheetContainer}
      ref={setWorksheetContainerRef}
    >
      <div className={classes.worksheetSidebarContainer}>
        <CreateWorksheetSidebarWrapper
          showSidebar={isSidebarOpen}
          toggleSidebar={handleSidebarCollapse}
          updateActiveQuestion={updateActiveQuestion}
          isWorksheetDetailsActive={
            activeQuestionId === WORKSHEET_QUESTION_CREATION
          }
          addQuestionButton={getAddButton({
            forSidebar: true,
            showAddInlineText: true,
          })}
        >
          <SideBar
            mode={mode}
            options={options}
            hideHeader={true}
            progress={progress}
            totalMarks={totalMarks}
            parentItemId={parentItemId}
            onAddOptionClick={addSection}
            questionCount={questionCount}
            runningProgress={runningProgress}
            parentResourceId={parentResourceId}
            isExtracting={isExtracting && isWorksheetExtractionInProgress}
          />
        </CreateWorksheetSidebarWrapper>
      </div>

      {/* This ID is used in QuestionLoader to createPortal */}
      <div
        id={WORKSHEET_ELEMENT_IDS.WORKSHEET_QUESTION_CONTENT_AREA_ID}
        className={contentClasses}
      >
        {isGenWithAIEnabled ||
        isGeneratingAIQuestions ||
        _.isEqual(activeQuestionId, GENERATE_USING_AI) ? (
          <div className={aiModalContainerStyles}>
            <AiQuestionGenerator
              assessmentId={parentId}
              addSection={addSection}
              toggleAiQuestionGenerator={toggleAiQuestionGenerator}
              sourceFrom={sourceFrom}
              classSubjects={classSubjects}
              creationMode={creationMode}
              mode={mode}
              isToddleLibraryPortal={isToddleLibraryPortal}
              portalType={portalType}
              aiConversationId={aiConversationId}
              aiPromptFromMetaData={aiPromptFromMetaData}
              courseId={courseId}
              selectedCourse={selectedCourse}
            />
          </div>
        ) : null}
        {showExtractFromDoc && (
          <ExtractFromDoc
            addSection={addSection}
            toggleAiQuestionGenerator={toggleAiQuestionGenerator}
            sourceFrom={sourceFrom}
            classSubjects={classSubjects}
            creationMode={creationMode}
            mode={mode}
            conversationMetadata={conversationMetadataExtract}
            conversationAttachments={conversationAttachmentsExtract}
            extractionConversationId={extractionConversationId}
            courseId={courseId}
            getExtractionAIConversationsRefetch={
              getExtractionAIConversationsRefetch
            }
            assessmentId={parentId}
            getAssessmentRefetch={getAssessmentRefetch}
            isWorksheetExtractionInProgress={isWorksheetExtractionInProgress}
          />
        )}

        {showGenerateWithAI ||
        showExtractFromDoc ||
        isTeacherPreviewModeEnabled ? null : (
          <SinglePageQuestionEditorComp
            t={t}
            mode={mode}
            setQuestionRef={setQuestionRef}
            showWorksheetAssistant={showWorksheetAssistant}
            classSubjects={classSubjects}
            parentId={parentId}
            portalType={portalType}
            assessmentId={parentId}
            creationMode={creationMode}
            unitPlanTemplateId={unitPlanTemplateId}
            getLearningGoalsMappingWrapper={getLearningGoalsMappingWrapper}
            worksheetDetailsRef={worksheetDetailsRef}
            courseId={courseId}
            selectedCourse={selectedCourse}
            unitPlanId={unitPlanId}
            isFromUnitPlan={isFromUnitPlan}
            isDraft={isDraft}
            sourceFrom={sourceFrom}
            sections={sections}
            getAddButton={getAddButton}
            assessmentDetailsComponent={assessmentDetailsComponent}
            assessmentToolConversationId={assessmentToolConversationId}
            assessmentTools={assessmentTools}
            assessmentDetails={assessmentDetails}
            getAssessmentRefetch={getAssessmentRefetch}
          />
        )}
      </div>
      {parentId && !_.isEmpty(conversationMetadataExtract) && (
        <ExtractRunningProgressUpdater
          parentId={parentId}
          progress={progress}
          runningProgress={runningProgress}
          updateExtractFromDoc={updateExtractFromDoc}
        />
      )}
    </div>
  );
});

const mapStateToProps = (state, ownProps) => {
  const { data: sections, parentId, isQueriesV2Enabled } = ownProps;

  const portalType = _.get(state, "login.userInfo.portalType");

  const defaultActive = _.get(state, "worksheet.sidebar.defaultActive", null);
  const activeQuestion = _.get(state, "worksheet.activeQuestion");
  const activeQuestionId = defaultActive ? defaultActive : activeQuestion;

  const isTeacherPreviewModeEnabled = _.get(
    state,
    "worksheet.isTeacherPreviewModeEnabled",
    false
  );
  const isGenWithAIEnabled = _.get(
    state,
    "worksheet.isAIQuestionGenerationEnabled",
    false
  );
  const isGeneratingAIQuestions = _.get(
    state,
    "worksheet.generatingAIQuestions",
    false
  );

  const isExtracting = _.get(
    state,
    `worksheet.extractFromDoc.${parentId}.isExtracting`,
    false
  );

  const progress = _.get(
    state,
    `worksheet.extractFromDoc.${parentId}.progress`,
    0
  );

  const runningProgress = _.get(
    state,
    `worksheet.extractFromDoc.${parentId}.runningProgress`,
    0
  );

  const isSidebarOpen = _.get(state, "worksheet.sidebar.isOpen", false);

  //WORKSHEET_RESTRUCTURING: Use getDetailsFromSectionsV2 when queriesV2 is enabled
  const _getDetailsFromSections = isQueriesV2Enabled
    ? getDetailsFromSectionsV2
    : getDetailsFromSections;
  const { questionResourceId, questionIndex, sectionId } =
    !_.isEmpty(activeQuestionId) &&
    _getDetailsFromSections(sections, activeQuestionId);

  const academicYearId = getSelectedAcademicYearId(state);
  const collaboratorsFilter = {
    userAcademicYearId: academicYearId,
  };
  const {
    unitPlanTemplateId,
    isWorksheetExtractionInProgress,
  } = getAssessmentDetailsData({
    assessmentId: parentId,
    collaboratorsFilter,
  });

  const shouldSetInitialValueAsDescription = _.get(
    state,
    "unitFlow.openAssessmentAIPaneOnMount",
    false
  );

  return {
    questionResourceId,
    questionIndex,
    sectionId,
    activeQuestionId,
    isGenWithAIEnabled,
    unitPlanTemplateId,
    isGeneratingAIQuestions,
    isTeacherPreviewModeEnabled,
    isSidebarOpen,
    isExtracting,
    progress,
    portalType,
    runningProgress,
    isWorksheetExtractionInProgress,
    collaboratorsFilter,
    shouldSetInitialValueAsDescription,
  };
};

const mapActionCreators = {
  createParentResource,
  createSection,
  importQuestionFromLibrary,
  updateLoadingState,
  updateActiveQuestion,
  createQuestion,
  updateIsAIQuestionGenerationEnabled,
  updateExtractFromDoc,
  updateWorksheetSidebar,
  createQuestionV2,
  importQuestionFromLibraryV2,
  updateWorksheetAIPanel,
};

CreateWorksheetV2.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  mode: PropTypes.oneOf(WORKSHEET_MODES),
  defaultQuestionIds: PropTypes.arrayOf(PropTypes.string),
  assessmentDetailsComponent: PropTypes.element,
  parentId: PropTypes.string,
  parentType: PropTypes.string,
  setQuestionRef: PropTypes.func,
  setWorksheetContainerRef: PropTypes.func,
  parentResourceId: PropTypes.string,
  showWorksheetAssistant: PropTypes.bool,
  sourceFrom: PropTypes.string,
  classSubjects: PropTypes.arrayOf(PropTypes.object),
  creationMode: PropTypes.string,
  isGenWithAIEnabled: PropTypes.bool,
  courseId: PropTypes.string,
  selectedCourse: PropTypes.object,
  unitPlanId: PropTypes.string,
  isFromUnitPlan: PropTypes.bool,
  isDraft: PropTypes.bool,
  isToddleLibraryPortal: PropTypes.bool,
  getAssessmentRefetch: PropTypes.func,
  getExtractionAIConversationsRefetch: PropTypes.func,
};

CreateWorksheetV2.defaultProps = {
  data: {},
  mode: MODE_TEACHER_CREATE,
  defaultQuestionIds: [],
  selectedCourse: {},
  isFromUnitPlan: false,
  isDraft: false,
  isToddleLibraryPortal: false,
  getAssessmentRefetch: _.noop,
  getExtractionAIConversationsRefetch: _.noop,
};

CreateWorksheetV2.displayName = "CreateWorksheetV2";

export default compose(
  connect(mapStateToProps, mapActionCreators),
  graphql(getAssessmentQuery, {
    name: "getAssessment",
    options: ({ parentId, portalType, collaboratorsFilter }) => ({
      // cache-first is used because this query will always get called in AssessmentDetails.js and latest data will be available in cache
      // if we use cache-and-network, it will make a network call and override the cache data
      // the reason to not use getAssessmentDetailsFromCache directly is this component won't get re-rendered if cache data changes
      fetchPolicy: "cache-first",
      variables: { id: parentId, portalType, collaboratorsFilter },
    }),
    props({ getAssessment }) {
      const assessmentDetails = _.get(getAssessment, "node", {});

      const aiConversationId = getAiConversationId(
        assessmentDetails,
        "TASK_CREATION"
      );

      const extractionConversationId = getAiConversationId(
        assessmentDetails,
        "WORKSHEET_EXTRACTION"
      );

      const assessmentToolConversationId = getAiConversationId(
        assessmentDetails,
        "ASSESSMENT_TOOL_CREATION"
      );

      const assessmentTools = _.filter(
        _.get(assessmentDetails, "assessmentTools.edges", []),
        tool => !_.isEqual(tool.node.assessmentToolType, "SCORE")
      );

      return {
        assessmentDetails,
        extractionConversationId,
        assessmentToolConversationId,
        aiConversationId,
        assessmentTools,
        getAssessmentRefetch: getAssessment?.refetch,
      };
    },
  }),
  graphql(getAIConversationsQuery, {
    name: "getAIConversations",
    skip: ({ aiConversationId }) => _.isEmpty(aiConversationId),
    options: ({ aiConversationId, portalType }) => ({
      fetchPolicy: "cache-and-network",
      variables: { id: aiConversationId, portalType },
      context: {
        useHttpLink: true,
      },
    }),
    props({
      getAIConversations,
      ownProps: {
        isData,
        isLoading,
        assessmentDetails,
        shouldSetInitialValueAsDescription,
      },
    }) {
      const queryData = getAIConversationsFromCache(
        getAIConversations.variables
      );
      const networkStatus = _.get(getAIConversations, "networkStatus");
      const conversationMetadata = _.get(queryData, "metadata", {});
      const conversationAttachments = _.get(queryData, "attachments", []);

      const aiPrompt = _.get(assessmentDetails, "metaData.aiPrompt", "");
      const aiPromptFromMetaData =
        !_.isEmpty(aiPrompt) &&
        shouldSetInitialValueAsDescription &&
        !_.has(conversationMetadata, "debugInfo")
          ? aiPrompt
          : "";

      return {
        aiPromptFromMetaData,
        isData: !_.isEmpty(queryData) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        conversationMetadata,
        conversationAttachments,
      };
    },
  }),
  graphql(getAIConversationsQuery, {
    name: "getExtractionAIConversations",
    skip: ({ extractionConversationId }) => _.isEmpty(extractionConversationId),
    options: ({ extractionConversationId, portalType }) => ({
      fetchPolicy: "cache-and-network",
      variables: { id: extractionConversationId, portalType },
      context: {
        useHttpLink: true,
      },
    }),
    props({
      getExtractionAIConversations,
      ownProps: { isData, isLoading, isWorksheetExtractionInProgress },
    }) {
      const queryData = getAIConversationsFromCache(
        getExtractionAIConversations.variables
      );
      const networkStatus = _.get(
        getExtractionAIConversations,
        "networkStatus"
      );

      const conversationMetadataExtract = isWorksheetExtractionInProgress
        ? _.get(queryData, `metadata.extractFromDocMetaData`, {})
        : null;
      const conversationAttachmentsExtract = _.get(
        conversationMetadataExtract,
        `attachments`,
        []
      );

      return {
        isData: !_.isEmpty(queryData) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        conversationMetadataExtract,
        conversationAttachmentsExtract,
        getExtractionAIConversationsRefetch:
          getExtractionAIConversations?.refetch,
      };
    },
  })
)(CreateWorksheetV2);
